import { Col, Row, Spin } from 'antd';
import PropType from 'prop-types';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { formatDate } from 'util/date';
import { getMachineType } from 'util/machine-type';
import { TRANSLATION_NAMESPACES } from 'util/translation/constant';
import {
  fetchMachineDetailsInformation,
  fetchMachineSuggestions
} from '../../state/operation';
import * as selector from '../../state/selector';
import { UpdateMachineField } from '../UpdateMachineField';

const columnLayoutProps = {
  lg: 12,
  xl: 12
};

export const MachineInformation = ({ id }) => {
  const { t } = useTranslation(TRANSLATION_NAMESPACES.MACHINE_DETAILS);
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(fetchMachineDetailsInformation(id));
  }, [dispatch, id]);
  const { machineDetailsInformationInfo } = useSelector((state) => ({
    machineDetailsInformationInfo:
      selector.selectMachineDetailsInformationInfo(state)
  }));

  const { data, isLoading } = machineDetailsInformationInfo;

  useEffect(() => {
    dispatch(fetchMachineSuggestions());
  }, [dispatch]);
  const { machineSuggestions } = useSelector((state) => ({
    machineSuggestions: selector.selectMachineSuggestions(state)
  }));

  const { facility: facilities = [], object: objects = [] } =
    machineSuggestions?.data || {};
  const facilityOptions = useMemo(
    () =>
      facilities.map((value) => ({
        value
      })),
    [facilities]
  );
  const objectOptions = useMemo(
    () =>
      objects.map((value) => ({
        value
      })),
    [objects]
  );

  return (
    <Spin spinning={isLoading}>
      {data && (
        <div className="machine-information">
          <span className="machine-information__title">
            {t('machineInformationPanel.title')}
          </span>
          <div className="machine-information__body">
            <div className="machine-information__heading">
              <Row className="machine-information__heading__rows">
                <Col
                  {...columnLayoutProps}
                  className="machine-information__heading__cols">
                  <p className="machine-information__heading__cols-text">
                    {t('machineInformationPanel.informationData')}
                  </p>
                </Col>
                <Col
                  {...columnLayoutProps}
                  className="machine-information__heading__cols">
                  <p className="machine-information__heading__cols-text">
                    {t('machineInformationPanel.corporateData')}
                  </p>
                </Col>
              </Row>
              <Row className="machine-information__body__rows">
                <Col
                  {...columnLayoutProps}
                  className="machine-information__body__cols">
                  <div className="machine-information__body__cols-text bold">
                    <h4>{t('machineInformationPanel.materialNumber')}</h4>
                    <span>{data.materialNumber}</span>
                  </div>
                </Col>
                <Col
                  {...columnLayoutProps}
                  className="machine-information__body__cols">
                  <div className="machine-information__body__cols-text bold">
                    <h4>{t('machineInformationPanel.facility')}</h4>
                    <UpdateMachineField
                      id={id}
                      fieldName="facility"
                      value={data.facility}
                      options={facilityOptions}
                    />
                  </div>
                </Col>
              </Row>
              <Row className="machine-information__body__rows">
                <Col
                  {...columnLayoutProps}
                  className="machine-information__body__cols">
                  <div className="machine-information__body__cols-text bold">
                    <h4>{t('machineInformationPanel.serialNumber')}</h4>
                    <span>{data.serialNumber}</span>
                  </div>
                </Col>
                <Col
                  {...columnLayoutProps}
                  className="machine-information__body__cols">
                  <div className="machine-information__body__cols-text bold">
                    <h4>{t('machineInformationPanel.object')}</h4>
                    <UpdateMachineField
                      id={id}
                      fieldName="object"
                      value={data.object}
                      options={objectOptions}
                    />
                  </div>
                </Col>
              </Row>
              <Row className="machine-information__body__rows">
                <Col
                  {...columnLayoutProps}
                  className="machine-information__body__cols">
                  <div className="machine-information__body__cols-text bold">
                    <h4>{t('machineInformationPanel.machineType')}</h4>
                    <span>{getMachineType(data.materialNumber)}</span>
                  </div>
                </Col>
                <Col
                  {...columnLayoutProps}
                  className="machine-information__body__cols">
                  <div className="machine-information__body__cols-text bold">
                    <hr className="machine-information__body--break" />
                    <h4>{t('machineInformationPanel.activationDate')}</h4>
                    <span>
                      {formatDate(data.firstActivationDate, {
                        shortFormat: true
                      })}
                    </span>
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        </div>
      )}
    </Spin>
  );
};

MachineInformation.propTypes = {
  id: PropType.string.isRequired
};
